type FixedString<N extends number> = { 0: string; length: N } & string

// YYYYMMP
export type PartPeriod = `${number}` & FixedString<7>

// YYYY-MM-DD
export type DateString = `${number}-${number}-${number}` & FixedString<10>

// YYYYMMDD
export type PricingDate = `${number}` & FixedString<8>

// Modified to correctly calculate part periods based on week number
const getPartPeriodWeekOfMonth = (date: Date) => {
  const dayOfMonth = date.getDate()

  // First week is always days 1-7, second is 8-14, third is 15-21, fourth is 22+ (rest of month)
  if (dayOfMonth <= 7) return 1
  if (dayOfMonth <= 14) return 2
  if (dayOfMonth <= 21) return 3
  return 4
}

export function dateToString(date: Date): DateString
export function dateToString(date: Date | null): DateString | undefined
export function dateToString(date: Date | undefined | null): DateString | undefined {
  return date?.toISOString().split('T')[0] as DateString | undefined
}

// format: YYYYMMP to { year: number; month: number; period: number }
export function splitPartPeriod(partPeriod: PartPeriod | string): { year: number; month: number; period: number } {
  if (partPeriod.length !== 7) {
    throw new Error(`Invalid period format '${partPeriod}' - expected YYYYMMP`)
  }

  const year = Number(partPeriod.slice(0, 4))
  const month = Number(partPeriod.slice(4, 6))
  const period = Number(partPeriod.slice(6, 7))

  if (Number.isNaN(period) || period < 1 || period > 4) {
    throw new Error(`Invalid week number '${period}' - expected 1-4`)
  }

  return { year, month, period }
}

// PartPeriod:
// The first part period of a month should start with the first day of the month.
// There can only be four part periods in a month.
// Part Period format is YYYYMMP (where P 1-4)

// format: YYYYMMP to Date
export function periodToDate(partPeriod: PartPeriod | string): Date {
  const { year, month, period } = splitPartPeriod(partPeriod)

  // Create the date with the specific year, month and day
  // For period 1: use day 1
  // For period 2: use day 8
  // For period 3: use day 15
  // For period 4: use day 22
  const day = 1 + (period - 1) * 7

  // Create a date using UTC to avoid timezone issues causing date shifts
  return new Date(Date.UTC(year, month - 1, day))
}

// format: YYYYMMP to YYYY-MM-DD
export function periodToString(period: PartPeriod): DateString {
  return dateToString(periodToDate(period))
}

// format: YYYYMMDD to YYYY-MM-DD
export function pricingDateToString(pricingDate: PricingDate | string): DateString {
  if (pricingDate.length !== 8) {
    throw new Error(`Invalid pricing date format '${pricingDate}' - expected YYYYMMDD`)
  }

  const year = pricingDate.slice(0, 4)
  const month = pricingDate.slice(4, 6)
  const day = pricingDate.slice(6, 8)

  return `${year}-${month}-${day}` as DateString
}

export function dateToPeriod(date: Date): PartPeriod {
  return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${getPartPeriodWeekOfMonth(date)}` as PartPeriod
}

// format: YYYY-MM-DD to YYYYMMP
export function stringToPeriod(dateString: DateString): PartPeriod {
  const date = stringToDate(dateString)
  return dateToPeriod(date)
}

// format: YYYY-MM-DD to YYYYMMDD
export function stringToPricingDate(dateString: DateString): PricingDate {
  return dateString.replace(/-/g, '') as PricingDate
}

// format: YYYY-MM-DD to Date
export function stringToDate(dateString: DateString): Date {
  return new Date(dateString)
}

export function isValidPricingDate(date: unknown): date is PricingDate {
  return typeof date === 'string' && /^\d{8}$/.test(date)
}

export function isValidDateString(date: unknown): date is DateString {
  return typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
}

export function isValidPeriod(date: unknown): date is PartPeriod {
  return typeof date === 'string' && /^\d{6}[1-4]$/.test(date)
}

export function formatPartPeriod(partPeriod: PartPeriod, options?: { excludeYear?: boolean }): string {
  const { year, month, period } = splitPartPeriod(partPeriod)

  if (options?.excludeYear) {
    return `${String(month).padStart(2, '0')}.${period}`
  }

  return `${year}.${String(month).padStart(2, '0')}.${period}`
}
